import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import PageHeader from "../components/PageHeader";
import StatTile from "../components/StatTile";
import DashboardCharts from "../components/DashboardCharts";
import { STATUS_OPTIONS } from "../lib/api";
import {
  LineChart,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

const STATUS_COLOR = STATUS_OPTIONS.reduce((acc, s) => {
  acc[s.value] = s.color;
  return acc;
}, {});

const FUN_GREETINGS = [
  "Welcome back, Doxico ninja",
  "Lights, camera, productivity",
  "Ready to make some magic",
  "Your studio missed you",
  "Let's ship some bangers today",
  "Coffee + cuts = chef's kiss",
  "Plot twist: today is a banger day",
];
const FUN_EMOJI = ["🎬", "✨", "🎯", "🚀", "🔥", "☕", "🎉", "🥳"];

function randomGreeting() {
  return {
    text: FUN_GREETINGS[Math.floor(Math.random() * FUN_GREETINGS.length)],
    emoji: FUN_EMOJI[Math.floor(Math.random() * FUN_EMOJI.length)],
  };
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [greet] = useState(randomGreeting);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/dashboard/overview");
        setData(r.data);
      } catch (e) {
        setData(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="p-10 text-neutral-500 text-sm uppercase tracking-[0.3em]">
        Loading overview…
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-10 text-neutral-500 text-sm">No data available.</div>
    );
  }

  const totalTasks = data.totals.tasks || 1;

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        eyebrow="Control Room"
        title="Studio Overview"
        description="Real-time productivity, deliverables, and revenue snapshot for your team."
      />

      <div className="px-6 lg:px-10 py-8 space-y-10">
        {/* Top stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-neutral-900 border border-neutral-900">
          <StatTile
            label="Total Tasks"
            value={data.totals.tasks}
            testId="stat-total-tasks"
            sub={`${data.status_counts.posted || 0} posted`}
          />
          <StatTile
            label="Clients"
            value={data.totals.clients}
            testId="stat-total-clients"
          />
          <StatTile
            label="Shoots"
            value={data.totals.shoots}
            testId="stat-total-shoots"
            sub={`₹ ${Number(data.totals.revenue || 0).toLocaleString()} earned`}
          />
          <StatTile
            label="Outstanding"
            value={`₹ ${Number(data.totals.outstanding || 0).toLocaleString()}`}
            accent="#F59E0B"
            testId="stat-outstanding"
          />
        </div>

        {/* Status breakdown + Weekly trend */}
        <DashboardCharts />

        {/* Status breakdown + Weekly trend */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-neutral-900 border border-neutral-900">
          <div className="bg-neutral-950 p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-6">
              Tasks by Status
            </div>
            <div className="space-y-5">
              {STATUS_OPTIONS.map((s) => {
                const count = data.status_counts[s.value] || 0;
                const pct = (count / totalTasks) * 100;
                return (
                  <div key={s.value}>
                    <div className="flex justify-between text-xs mb-1.5">
                      <span className="uppercase tracking-[0.15em] text-neutral-400">
                        {s.label}
                      </span>
                      <span className="text-neutral-300 font-mono">{count}</span>
                    </div>
                    <div className="h-1 bg-neutral-900 overflow-hidden">
                      <div
                        className="h-full transition-all"
                        style={{ width: `${pct}%`, background: s.color }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-neutral-950 p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-6">
              Posted · Last 7 days
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.weekly_trend}>
                  <CartesianGrid stroke="#171717" vertical={false} />
                  <XAxis
                    dataKey="date"
                    stroke="#525252"
                    tick={{ fontSize: 10, fill: "#737373" }}
                    tickFormatter={(d) => d.slice(5)}
                  />
                  <YAxis
                    stroke="#525252"
                    tick={{ fontSize: 10, fill: "#737373" }}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "#0A0A0A",
                      border: "1px solid #262626",
                      borderRadius: 0,
                      fontSize: 12,
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="posted"
                    stroke="#00C97A"
                    strokeWidth={2}
                    dot={{ fill: "#00C97A", r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Productivity tables */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="border border-neutral-900">
            <div className="px-5 py-4 border-b border-neutral-900 flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                Editor Productivity
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-600">
                Total / Active / Posted
              </div>
            </div>
            <div className="divide-y divide-neutral-900">
              {data.editors.length === 0 && (
                <div className="px-5 py-6 text-sm text-neutral-500">
                  No editor activity yet.
                </div>
              )}
              {data.editors.map((e) => (
                <div
                  key={e.user_id}
                  className="px-5 py-4 flex items-center justify-between hover:bg-neutral-900/60"
                  data-testid={`editor-row-${e.user_id}`}
                >
                  <div className="text-sm font-medium">{e.name}</div>
                  <div className="flex gap-6 text-xs font-mono">
                    <span className="text-white">{e.total}</span>
                    <span className="text-blue-400">{e.in_progress}</span>
                    <span className="text-neutral-300">{e.posted}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="border border-neutral-900">
            <div className="px-5 py-4 border-b border-neutral-900 flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                SMM Productivity
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-600">
                Assigned / Posted
              </div>
            </div>
            <div className="divide-y divide-neutral-900">
              {data.smms.length === 0 && (
                <div className="px-5 py-6 text-sm text-neutral-500">
                  No SMM activity yet.
                </div>
              )}
              {data.smms.map((e) => (
                <div
                  key={e.user_id}
                  className="px-5 py-4 flex items-center justify-between hover:bg-neutral-900/60"
                  data-testid={`smm-row-${e.user_id}`}
                >
                  <div className="text-sm font-medium">{e.name}</div>
                  <div className="flex gap-6 text-xs font-mono">
                    <span className="text-white">{e.total}</span>
                    <span className="text-neutral-300">{e.posted}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
